import React, { useState, useEffect, useRef } from 'react';
import { exportProjectToExcel } from './ExcelExport';
import { exportConditionReportV3 } from './conditionReportV3';
import { exportPhotosAsZip } from './PhotoExport';
import ScopePDFPreview from './ScopePDFPreview';
import { entryM2, surfaceUnit, calcAreaHours, DEFAULT_GIRTHS, isUserMeasured } from './calcUtils';
import ConfirmModal from './ConfirmModal';
import { WORKFLOW } from './dataModel';
import { SECTORS } from './areaConstants';
import { db, auth } from './firebase';
import { collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore';
import Icon from './Icons';
import { showToast } from './Toast';
import { collectCoverCandidates } from './coverPhotoUtils';

// ─── Action icons for activity log ──────────────────────────────────────────
const ACTION_ICONS = {
  created: '🆕', measured: '📐', assessed: '📋', assessment_complete: '✅',
  adjusted: '⚖️', exported: '⬇️', duplicated: '⧉', archived: '📦',
};

function formatAgo(isoStr) {
  const d = new Date(isoStr);
  const now = new Date();
  const mins = Math.round((now - d) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
}

// ─── Activity Log component ─────────────────────────────────────────────────
function ActivityLog({ projectId }) {
  const [entries, setEntries] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!projectId) return;
    // Try ordered query first; fall back to unordered if index missing
    const q = query(
      collection(db, 'projects', projectId, 'activity'),
      orderBy('at', 'desc'),
      limit(50)
    );
    const unsub = onSnapshot(q, (snap) => {
      setEntries(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setError(null);
    }, (err) => {
      console.warn('Activity query failed, trying unordered:', err.message);
      // Fallback — no orderBy (works without index)
      const fallbackQ = query(
        collection(db, 'projects', projectId, 'activity'),
        limit(50)
      );
      const unsub2 = onSnapshot(fallbackQ, (snap) => {
        const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        docs.sort((a, b) => (b.at || '').localeCompare(a.at || ''));
        setEntries(docs);
        setError(null);
      }, (err2) => {
        console.warn('Activity fallback also failed:', err2.message);
        setError(err2.message);
      });
      return () => unsub2();
    });
    return () => unsub();
  }, [projectId]);

  const visible = expanded ? entries : entries.slice(0, 5);

  return (
    <div className="al-section">
      <div className="al-header" onClick={() => entries.length > 0 && setExpanded(v => !v)}>
        <span className="al-title">📋 Activity History</span>
        {entries.length > 0 && (
          <span className="al-toggle">{expanded ? '▾ Less' : `▸ All (${entries.length})`}</span>
        )}
      </div>
      {entries.length === 0 ? (
        <div className="al-empty">{error ? '⚠ Firestore rules need updating — add read access for the activity subcollection. See browser console for details.' : 'No activity recorded yet. Actions like measuring, assessing, and exporting will appear here.'}</div>
      ) : (
        <div className="al-list">
          {visible.map(e => (
            <div key={e.id} className="al-entry">
              <span className="al-icon">{ACTION_ICONS[e.action] || '•'}</span>
              <div className="al-body">
                <span className="al-who">{e.who}</span>
                <span className="al-detail">{e.detail}</span>
              </div>
              <span className="al-time">{formatAgo(e.at)}</span>
            </div>
          ))}
        </div>
      )}
      {!expanded && entries.length > 5 && (
        <button className="al-show-more" onClick={() => setExpanded(true)}>
          Show all {entries.length} entries
        </button>
      )}
    </div>
  );
}

// ─── Inline project details editor ────────────────────────────────────────────
function ProjectDetailsEditor({ project, onSave, onCancel }) {
  const [form, setForm] = useState({
    jobNumber:     project.jobNumber     || '',
    clientName:    project.clientName    || '',
    projectName:   project.projectName   || '',
    address:       project.address       || '',
    estimatorName: project.estimatorName || '',
    bdmName:       project.bdmName       || '',
    date:          project.date          || '',
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.clientName.trim() && !form.projectName.trim()) {
      showToast('Please enter a client name or project name', 'warning');
      return;
    }
    setSaving(true);
    try {
      await onSave({ ...project, ...form });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pde-form">
      <div className="pde-row">
        <label>Job No.</label>
        <input value={form.jobNumber} onChange={e => set('jobNumber', e.target.value)} placeholder="e.g. 2024-0142" />
      </div>
      <div className="pde-row">
        <label>Client <span className="pde-req">*</span></label>
        <input value={form.clientName} onChange={e => set('clientName', e.target.value)} placeholder="e.g. Brisbane City Council" />
      </div>
      <div className="pde-row">
        <label>Address</label>
        <input value={form.address} onChange={e => set('address', e.target.value)} placeholder="e.g. 123 Main St" />
      </div>
      <div className="pde-row">
        <label>Project</label>
        <input value={form.projectName} onChange={e => set('projectName', e.target.value)} placeholder="e.g. Block B repaint" />
      </div>
      <div className="pde-row">
        <label>Estimator</label>
        <input value={form.estimatorName} onChange={e => set('estimatorName', e.target.value)} placeholder="Your name" />
      </div>
      <div className="pde-row">
        <label>BDM</label>
        <input value={form.bdmName} onChange={e => set('bdmName', e.target.value)} placeholder="BDM name" />
      </div>
      <div className="pde-row">
        <label>Date</label>
        <input type="date" value={form.date} onChange={e => set('date', e.target.value)} />
      </div>
      <div className="pde-actions">
        <button className="pde-cancel-btn" onClick={onCancel} disabled={saving}>Cancel</button>
        <button className="pde-save-btn" onClick={handleSave} disabled={saving}>
          {saving ? <><span className="spinner spinner-sm spinner-white"></span> Saving…</> : 'Save changes'}
        </button>
      </div>
    </div>
  );
}


function buildSummary(areas) {
  const surfaces = {};
  const substrates = {};

  areas.forEach(area => {
    const gc = {};
    (area.repeatGroups || []).forEach(g => { gc[g.id] = g.count || 1; });
    const bySubstrate = {};
    (area.surfaceEntries || []).forEach(e => {
      const k = e.substrate; if (!k) return;
      if (!bySubstrate[k]) bySubstrate[k] = [];
      bySubstrate[k].push(e);
    });
    const isInt = area.type === 'internal';
    const target = isInt ? surfaces : substrates;

    Object.entries(bySubstrate).forEach(([name, entries]) => {
      const unit = surfaceUnit(name);
      if (!target[name]) target[name] = { total: 0, unit };
      const base = area.surfaceSettings?.[name]?.baseSurface || name;
      const girth = area.surfaceSettings?.[name]?.girth ?? DEFAULT_GIRTHS[base] ?? 1;
      entries.forEach(e => {
        const rc = e.repeatGroupId ? (gc[e.repeatGroupId] || 1) : 1;
        target[name].total += entryM2(name, e) * girth * rc;
      });
    });
  });

  return { surfaces, substrates };
}

// ─── Area card — used on both mobile and desktop ──────────────────────────────
function AreaCard({ area, onEdit, onRequestDelete, onRequestDuplicate, projectWorkflow, onAssessArea }) {
  const isFinished = area.status === 'finished';

  // Assessment status for this area
  const wf = projectWorkflow || 'measuring';
  const inAssessPhase = wf !== 'measuring';
  const assessData = area.assessBySubstrate || {};
  const assessValues = Object.values(assessData);
  const hasAnyAssess = assessValues.some(v => parseFloat(v.hours) > 0);
  const measuredSubstrates = [...new Set((area.surfaceEntries || []).map(e => e.substrate))];
  const pmAddedSubs = (area.pmAddedSurfaces || []).filter(s => !measuredSubstrates.includes(s));
  const allSubstrates = [...measuredSubstrates, ...pmAddedSubs];
  const allAssessed = allSubstrates.length > 0 && allSubstrates.every(s => parseFloat(assessData[s]?.hours) > 0);

  const surfaceLabel = () => {
    const measured = [...new Set((area.surfaceEntries || []).map(e => e.substrate))];
    const pmAdded = (area.pmAddedSurfaces || []).filter(s => !measured.includes(s));
    const pmSurfaces = (area.pmSurfaces || []).filter(s => !measured.includes(s) && !pmAdded.includes(s));
    const all = [...measured, ...pmAdded.map(s => `${s} ⚠`), ...pmSurfaces.map(s => `${s} ⚠`)];
    return all.join(', ') || (area.pmAdded ? 'Needs measuring' : '—');
  };

  // Status display logic — always show measuring status, plus assessment if it exists
  const statusJsx = () => {
    let measLabel, measClass;
    if (isFinished && area.manualComplete) {
      // Manually accepted as complete — not all surfaces measured
      measLabel = '✓ Accepted';
      measClass = 'accepted';
    } else if (isFinished) {
      measLabel = '✓ Measured';
      measClass = 'done';
    } else {
      measLabel = 'In progress';
      measClass = 'wip';
    }

    // Assessment status — show if workflow is past measuring OR assessment data exists
    const projectHasAssessment = wf !== 'measuring' || hasAnyAssess;
    let assessLabel = null;
    if (projectHasAssessment) {
      if (allAssessed) assessLabel = '✓ Assessed';
      else if (hasAnyAssess) assessLabel = 'Partial';
      else assessLabel = 'Not assessed';
    }

    return (
      <div className="pd-area-status-row">
        <span className={`pd-area-status ${measClass}`}>📐 {measLabel}</span>
        {assessLabel && <span className={`pd-area-status ${allAssessed ? 'assessed' : hasAnyAssess ? 'partial' : 'not-assessed'}`}>📋 {assessLabel}</span>}
      </div>
    );
  };

  const handleClick = () => {
    if (inAssessPhase && onAssessArea) onAssessArea(area);
    else onEdit(area);
  };

  return (
    <div className={`pd-area-card ${isFinished ? 'pd-area-finished' : ''}`} onClick={handleClick}>
      <div className="pd-area-top">
        <div className="pd-area-name-row">
          <span className="pd-area-icon">{area.type === 'internal' ? '🏠' : '🏗️'}</span>
          <span className="pd-area-name">{area.name || 'Unnamed Area'}</span>
          {area.pmAdded && <span className="pd-area-pm-badge">PM Added</span>}
          {area.tag && <span className="pd-area-tag">{area.tag}</span>}
        </div>
        {statusJsx()}
      </div>
      <div className="pd-area-surfaces">{surfaceLabel()}</div>
      <div className="pd-area-footer">
        <span className="pd-area-meta">
          {(() => { const p = calcAreaHours(area); return p.total > 0 ? <span className="pd-area-hours">⏱ {p.total.toFixed(0)}h · {p.litres.toFixed(0)}L{p.groupBreakdown?.length > 0 && <small className="pd-area-group-hrs"> (incl. {p.groupBreakdown.map(g => `${g.hours.toFixed(0)}h ${g.name}`).join(', ')})</small>}</span> : null; })()}
          {(area.photos || []).length > 0 && `📷 ${area.photos.length}`}
          {area.scopeNotes && ' 💬'}
        </span>
        <div className="pd-area-actions" onClick={e => e.stopPropagation()}>
          {!inAssessPhase && (
            <>
              <button className="pd-area-btn" onClick={() => onRequestDuplicate(area)} title="Duplicate" aria-label="Duplicate area"><Icon name="copy" size={14} /></button>
              <button className="pd-area-btn danger" onClick={() => onRequestDelete(area)} title="Delete" aria-label="Delete area"><Icon name="trash" size={14} /></button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Desktop export + summary panel ───────────────────────────────────────────
function ExportPanel({ project, onEditArea, onSaveDetails, onReview, workflow, onArchive, onDelete, canEdit, currentUser, onAssess, allAreasAssessed, onLogActivity, activeRole }) {
  const isAssessor = activeRole === 'assessor';
  const [excelBusy, setExcelBusy] = useState(false);
  const [excelDone, setExcelDone] = useState(false);
  const [showPDF, setShowPDF]     = useState(false);
  const [showReportMeta, setShowReportMeta] = useState(false);
  const [meta, setMeta] = useState(() => ({
    siteName: '', recipientName: '', recipientTitle: '', inspectionDate: '',
    higginsRep: '', siteDirector: '',
    assessorName: '', clientContact: '', sector: '',
    overallConditionOverride: '', clientLogo: '', coverPhotoUrl: '', keyRisksOverride: [],
    buildingAge: '', notableItems: '',
    branchName: '', branchAddress: '', branchPhone: '', branchEmail: '', branchAbn: '',
    ...(project.reportMeta || {}),
  }));
  const saveMeta = () => {
    // Remember branch block for next time (per-device convenience pre-fill)
    try {
      const branch = {
        branchName: meta.branchName, branchAddress: meta.branchAddress,
        branchPhone: meta.branchPhone, branchEmail: meta.branchEmail, branchAbn: meta.branchAbn,
      };
      window.localStorage.setItem('s2s_branch_defaults', JSON.stringify(branch));
    } catch (e) { /* ignore storage errors */ }
    // onSaveDetails expects a single object that includes firestoreId (generic merge)
    onSaveDetails?.({ firestoreId: project.firestoreId, reportMeta: meta });
    showToast('Report details saved', 'success');
    setShowReportMeta(false);
  };
  const loadBranchDefaults = () => {
    try {
      const saved = JSON.parse(window.localStorage.getItem('s2s_branch_defaults') || '{}');
      setMeta(m => ({ ...m, ...saved }));
    } catch (e) { /* ignore */ }
  };
  const [editingDetails, setEditingDetails] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoMsg, setPhotoMsg]   = useState('');

  const areas    = project.areas || [];
  const internal = areas.filter(a => a.type === 'internal');
  const external = areas.filter(a => a.type === 'external');
  const photos   = areas.reduce((s, a) => s + (a.photos || []).length, 0);
  const { surfaces, substrates } = buildSummary(areas);

  const hasSurfaces   = Object.keys(surfaces).length > 0;
  const hasSubstrates = Object.keys(substrates).length > 0;

  const handleExcel = async (excludeSound = false) => {
    setExcelBusy(true);
    try {
      const idToken = await auth.currentUser?.getIdToken();
      await exportProjectToExcel(project, { excludeSound, idToken });
      setExcelDone(true);
      setTimeout(() => setExcelDone(false), 3000);
      onLogActivity?.('exported', excludeSound ? 'Excel (Exclude Sound)' : 'Excel (Measured)');
    } catch (err) {
      showToast('Excel export failed: ' + err.message, 'error');
    }
    setExcelBusy(false);
  };

  const [crBusy, setCrBusy] = useState(false);
  // Full report vs partial: the partial keeps every summary section but cuts the
  // Detailed Condition Register to the Amber/Red areas where painting work is needed.
  const [crPriorityOnly, setCrPriorityOnly] = useState(false);
  const handleConditionReport = async () => {
    setCrBusy(true);
    try {
      const idToken = await auth.currentUser?.getIdToken();
      const name = await exportConditionReportV3(project, { idToken, priorityOnly: crPriorityOnly });
      onLogActivity?.('exported', crPriorityOnly ? 'Condition Report (Word, Amber & Red detail)' : 'Condition Report (Word)');
      showToast('Condition Report generated: ' + name, 'success');
    } catch (err) {
      showToast('Condition Report failed: ' + err.message, 'error');
    }
    setCrBusy(false);
  };

  // Rendered in both the assessor and measurer sidebars — one definition so the
  // toggle and the button it drives can never drift apart between the two.
  const conditionReportBlock = (
    <>
      <button className="ep-btn-cr" onClick={handleConditionReport} disabled={crBusy}>
        {crBusy ? '⏳ Generating…' : crPriorityOnly ? '📄 Condition Report — Amber & Red' : '📄 Condition Report (Word)'}
      </button>
      <label className="ep-cr-toggle"
        title="The Area Summary still lists every area. Only the detail pages (with photos) are narrowed to the areas needing painting work.">
        <input type="checkbox" checked={crPriorityOnly} disabled={crBusy}
          onChange={e => setCrPriorityOnly(e.target.checked)} />
        <span>Detail Amber &amp; Red areas only</span>
      </label>
    </>
  );

  const handlePhotoExport = async () => {
    setPhotoBusy(true); setPhotoMsg('');
    try {
      await exportPhotosAsZip(project, msg => setPhotoMsg(msg));
      onLogActivity?.('exported', 'Photos (.zip)');
      setTimeout(() => { setPhotoBusy(false); setPhotoMsg(''); }, 3000);
    } catch (err) {
      showToast('Photo export failed: ' + err.message, 'error');
      setPhotoBusy(false); setPhotoMsg('');
    }
  };

  // ── Assessor: compute assessment progress for sidebar ──
  const measurableAreas = areas.filter(a => (a.surfaceEntries || []).length > 0);
  const assessedAreas = measurableAreas.filter(a => {
    const ad = a.assessBySubstrate || {};
    const subs = [...new Set((a.surfaceEntries || []).map(e => e.substrate).filter(Boolean))];
    return subs.length > 0 && subs.every(s => parseFloat(ad[s]?.hours) > 0);
  });
  const inAssessWorkflow = workflow === 'ready_to_assess' || workflow === 'assessing';
  const hasAnyAssessData = areas.some(a => {
    const ad = a.assessBySubstrate || {};
    return Object.values(ad).some(v => parseFloat(v.hours) > 0);
  });

  return (
    <div className="ep-panel">
      {showPDF && <ScopePDFPreview project={project} onClose={() => setShowPDF(false)} />}

      {canEdit && (
        <div className="ep-reportmeta">
          <button className="ep-reportmeta-toggle" onClick={() => setShowReportMeta(s => !s)}>
            {showReportMeta ? '▾' : '▸'} Report Details (for Condition Report / Proposal)
          </button>
          {showReportMeta && (
            <div className="ep-reportmeta-body">
              <label>Site name (report title)
                <input value={meta.siteName} placeholder={project.projectName || project.clientName || ''}
                  onChange={e => setMeta(m => ({ ...m, siteName: e.target.value }))} />
              </label>
              <div className="ep-meta-row">
                <label>Recipient name
                  <input value={meta.recipientName} placeholder="e.g. Renee Griffin"
                    onChange={e => setMeta(m => ({ ...m, recipientName: e.target.value }))} />
                </label>
                <label>Recipient title
                  <input value={meta.recipientTitle} placeholder="e.g. Capital Works Project Manager"
                    onChange={e => setMeta(m => ({ ...m, recipientTitle: e.target.value }))} />
                </label>
              </div>
              <div className="ep-meta-row">
                <label>Inspection date
                  <input type="date" value={meta.inspectionDate}
                    onChange={e => setMeta(m => ({ ...m, inspectionDate: e.target.value }))} />
                </label>
                <label>Higgins representative
                  <input value={meta.higginsRep} placeholder={project.bdmName || project.estimatorName || ''}
                    onChange={e => setMeta(m => ({ ...m, higginsRep: e.target.value }))} />
                </label>
              </div>
              <label>Site director / representative
                <input value={meta.siteDirector} placeholder="e.g. Kylie"
                  onChange={e => setMeta(m => ({ ...m, siteDirector: e.target.value }))} />
              </label>
              <div className="ep-meta-row">
                <label>Assessor name
                  <input value={meta.assessorName} placeholder={meta.higginsRep || project.bdmName || ''}
                    onChange={e => setMeta(m => ({ ...m, assessorName: e.target.value }))} />
                </label>
                <label>Client contact
                  <input value={meta.clientContact} placeholder="e.g. Facilities manager"
                    onChange={e => setMeta(m => ({ ...m, clientContact: e.target.value }))} />
                </label>
              </div>
              <div className="ep-meta-row">
                <label>Sector
                  <select value={meta.sector || ''}
                    onChange={e => setMeta(m => ({ ...m, sector: e.target.value }))}>
                    <option value="">— Not specified —</option>
                    {SECTORS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                  </select>
                </label>
                <label>Building age
                  <input value={meta.buildingAge} placeholder="e.g. 1980s, ~15 years"
                    onChange={e => setMeta(m => ({ ...m, buildingAge: e.target.value }))} />
                </label>
              </div>
              <label>Notable items (woven into the summary)
                <input value={meta.notableItems} placeholder="e.g. Community centre roof recently recoated"
                  onChange={e => setMeta(m => ({ ...m, notableItems: e.target.value }))} />
              </label>
              <label>Cover photo (front page of the Condition Report)</label>
              {(() => {
                const candidates = collectCoverCandidates(project);
                if (!candidates.length) {
                  return <div className="ep-meta-empty">Add photos to an area to choose a cover.</div>;
                }
                const tile = (selected) => ({
                  padding: 0, width: 84, height: 84, borderRadius: 6, cursor: 'pointer',
                  background: 'transparent',
                  border: selected ? '3px solid #5B2D82' : '1px solid #D8DCE1',
                });
                return (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
                    <button type="button" style={tile(!meta.coverPhotoUrl)}
                      onClick={() => setMeta(m => ({ ...m, coverPhotoUrl: '' }))}>None</button>
                    {candidates.map(c => (
                      <button key={c.fullUrl} type="button" style={tile(meta.coverPhotoUrl === c.fullUrl)}
                        title={`${c.areaName}${c.substrate ? ' — ' + c.substrate : ''}`}
                        onClick={() => setMeta(m => ({ ...m, coverPhotoUrl: c.fullUrl }))}>
                        <img src={c.thumbUrl} alt={`${c.areaName} ${c.substrate}`}
                          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', borderRadius: 4 }} />
                      </button>
                    ))}
                  </div>
                );
              })()}

              <div className="ep-meta-branchhdr">
                <span>Branch details</span>
                <button type="button" className="ep-meta-loadbranch" onClick={loadBranchDefaults}>Use last branch</button>
              </div>
              <label>Branch name
                <input value={meta.branchName} placeholder="e.g. Higgins Coatings — Brisbane"
                  onChange={e => setMeta(m => ({ ...m, branchName: e.target.value }))} />
              </label>
              <label>Branch address
                <input value={meta.branchAddress} placeholder="e.g. 52 Bishop Street, Kelvin Grove QLD 4059"
                  onChange={e => setMeta(m => ({ ...m, branchAddress: e.target.value }))} />
              </label>
              <div className="ep-meta-row">
                <label>Branch phone
                  <input value={meta.branchPhone} placeholder="07 3552 8300"
                    onChange={e => setMeta(m => ({ ...m, branchPhone: e.target.value }))} />
                </label>
                <label>Branch email
                  <input value={meta.branchEmail} placeholder="brisbane@higgins.com.au"
                    onChange={e => setMeta(m => ({ ...m, branchEmail: e.target.value }))} />
                </label>
              </div>
              <label>Branch ABN
                <input value={meta.branchAbn} placeholder="50 005 632 708"
                  onChange={e => setMeta(m => ({ ...m, branchAbn: e.target.value }))} />
              </label>

              <button className="ep-meta-save" onClick={saveMeta}>Save report details</button>
            </div>
          )}
        </div>
      )}

      {/* Project meta */}
      <div className="ep-meta">
        <div className="ep-meta-header">
          <span className="ep-meta-title">Project Details</span>
          {!isAssessor && (
            <button className="ep-edit-btn" onClick={() => setEditingDetails(e => !e)}>
              {editingDetails ? <><Icon name="close" size={13} /> Cancel</> : <><Icon name="edit" size={13} /> Edit Project Details</>}
            </button>
          )}
        </div>
        {editingDetails && !isAssessor ? (
          <ProjectDetailsEditor
            project={project}
            onSave={async (updated) => { await onSaveDetails(updated); setEditingDetails(false); }}
            onCancel={() => setEditingDetails(false)}
          />
        ) : (
          <>
            {project.estimatorName && <div className="ep-meta-row"><span>👤</span>{project.estimatorName}</div>}
            {project.date          && <div className="ep-meta-row"><span>📅</span>{project.date}</div>}
            {project.address       && <div className="ep-meta-row"><span>📍</span>{project.address}</div>}
            {project.jobNumber     && <div className="ep-meta-row"><span>🔖</span>#{project.jobNumber}</div>}
            {project.createdByName && <div className="ep-meta-row"><span>✏️</span>Created by: {project.createdByName}</div>}
            {(project.measuredByNames || []).length > 0 && (
              <div className="ep-meta-row"><span>📐</span>Measured by: {project.measuredByNames.join(', ')}</div>
            )}
            {(project.assessedByNames || []).length > 0 && (
              <div className="ep-meta-row"><span>📋</span>Assessed by: {project.assessedByNames.join(', ')}</div>
            )}
          </>
        )}
      </div>

      {/* Activity log — right after project details for visibility */}
      <ActivityLog projectId={project.firestoreId} />

      {/* ════ ASSESSOR SIDEBAR ════ */}
      {isAssessor ? (
        <>
          {/* Assessment status */}
          {inAssessWorkflow && (
            <div className="ep-assess-section">
              <div className="ep-section-label">Assessment</div>
              <div className="ep-assess-progress">
                <div className="ep-assess-progress-text">{assessedAreas.length} of {measurableAreas.length} areas assessed</div>
                <div className="as-progress-track" style={{marginTop:6}}>
                  <div className="as-progress-fill" style={{ width: `${measurableAreas.length > 0 ? (assessedAreas.length / measurableAreas.length) * 100 : 0}%` }} />
                </div>
              </div>
              {allAreasAssessed ? (
                <>
                  <button className="pd-acc-btn" style={{marginTop:12}} onClick={() => onAssess?.(null)}>
                    ✉️ Notify BDM Now →
                  </button>
                  <button className="ep-assess-amend-btn" onClick={() => onAssess?.(null)}>
                    ← Review / Amend Assessment
                  </button>
                </>
              ) : (
                <button className="ep-assess-continue-btn" onClick={() => onAssess?.(null)}>
                  📋 Continue Assessing →
                </button>
              )}
            </div>
          )}
          {workflow === 'assessed' && (
            <div className="ep-assess-section">
              <div className="ep-section-label">Assessment</div>
              <div className="ep-assess-done-msg">✅ Assessment complete — BDM has been notified</div>
              <button className="ep-assess-amend-btn" onClick={() => onAssess?.(null)}>
                ← Review / Amend Assessment
              </button>
            </div>
          )}

          {/* Scope PDF only */}
          <div className="ep-export-section">
            <div className="ep-section-label">Export</div>
            <button className="ep-btn-pdf" onClick={() => { setShowPDF(true); onLogActivity?.("exported", "Scope PDF"); }}>
              📄 Scope PDF
            </button>
            {conditionReportBlock}
            {hasAnyAssessData && onReview && (
              <button className="ep-btn-review" onClick={onReview}>
                ⚖️ Compare Hours (View Only)
              </button>
            )}
          </div>
        </>
      ) : (
        /* ════ MEASURER SIDEBAR ════ */
        <>
          {/* Archive / Delete */}
          <div className="ep-danger-row">
            <button className="ep-archive-btn" onClick={onArchive}><Icon name="archive" size={14} /> Archive</button>
            {canEdit !== false && <button className="ep-delete-btn" onClick={onDelete}><Icon name="trash" size={14} /> Delete</button>}
          </div>

          {/* Stats strip */}
          <div className="ep-stats">
            <div className="ep-stat"><span>{internal.length}</span>Internal</div>
            <div className="ep-stat"><span>{external.length}</span>External</div>
            <div className="ep-stat"><span>{photos}</span>Photos</div>
          </div>

          {/* Export buttons */}
          <div className="ep-export-section">
            <div className="ep-section-label">Export</div>
            <button className="ep-btn-excel" onClick={() => handleExcel(false)} disabled={excelBusy}>
              {excelBusy ? <><span className="spinner spinner-sm spinner-white"></span> Generating...</> : excelDone ? <><Icon name="check" size={14} /> Excel Downloaded</> : <><Icon name="download" size={14} /> Export to Excel (Measured)</>}
            </button>
            <button className="ep-btn-excel ep-btn-excel-excl" onClick={() => handleExcel(true)} disabled={excelBusy}>
              {excelBusy ? <><span className="spinner spinner-sm spinner-white"></span> Generating...</> : <><Icon name="download" size={14} /> Export to Excel — Exclude Sound</>}
            </button>
            <button className="ep-btn-pdf" onClick={() => { setShowPDF(true); onLogActivity?.("exported", "Scope PDF"); }}>
              📄 Scope PDF
            </button>
            {conditionReportBlock}
            {photos > 0 && (
              <button className="ep-btn-photos" onClick={handlePhotoExport} disabled={photoBusy}>
                {photoBusy ? <><span className="spinner spinner-sm spinner-white"></span> {photoMsg}</> : <><Icon name="camera" size={14} /> Export Photos (.zip)</>}
              </button>
            )}
            {hasAnyAssessData && onReview && (
              <button className="ep-btn-review" onClick={onReview}>
                ⚖️ Compare Hours {workflow === 'approved' ? '(Adjusted)' : `(${assessedAreas.length}/${measurableAreas.length} assessed)`}
              </button>
            )}
          </div>
        </>
      )}

      {/* Measurement summary (QA) — both roles */}
      {(hasSurfaces || hasSubstrates) && (
        <div className="ep-summary-section">
          <div className="ep-section-label">Measurement Summary</div>

          {hasSurfaces && (
            <table className="ep-summary-table">
              <thead>
                <tr>
                  <th colSpan="2" className="ep-table-group-hdr">🏠 Internal</th>
                </tr>
                <tr><th>Surface</th><th>Total</th></tr>
              </thead>
              <tbody>
                {Object.entries(surfaces).map(([name, { total, unit }]) => (
                  <tr key={name}>
                    <td>{name}</td>
                    <td className="ep-total">{total > 0 ? `${total.toFixed(2)} ${unit}` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {hasSubstrates && (
            <table className="ep-summary-table" style={{ marginTop: hasSurfaces ? 12 : 0 }}>
              <thead>
                <tr>
                  <th colSpan="2" className="ep-table-group-hdr ext">🏗️ External</th>
                </tr>
                <tr><th>Substrate</th><th>Total</th></tr>
              </thead>
              <tbody>
                {Object.entries(substrates).map(([name, { total }]) => (
                  <tr key={name}>
                    <td>{name}</td>
                    <td className="ep-total ext">{total > 0 ? `${total.toFixed(2)} m²` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <p className="ep-summary-note">Totals are aggregated across all areas</p>
        </div>
      )}

      {!hasSurfaces && !hasSubstrates && areas.length > 0 && (
        <div className="ep-no-data">No measurements recorded yet</div>
      )}
    </div>
  );
}

// ─── Main ProjectDetail ────────────────────────────────────────────────────────
function ProjectDetail({ project, onBack, onNewArea, onEditArea, onDeleteArea, onDuplicateArea, onDelete, onArchive, onSaveDetails, onSetWorkflow, onAssess, onReview, onAcceptIncompleteAreas, currentUser, onSignOut, onUpdateDisplayName, onLogActivity, activeRole, onSetActiveRole }) {
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  const [confirm, setConfirm] = useState(null);
  const [jumpNavOpen, setJumpNavOpen] = useState(false);
  const [archiveToast, setArchiveToast] = useState(false);
  const [showMobileEdit, setShowMobileEdit] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState(currentUser?.displayName || '');
  // Only the project owner (BDM who measured it) can archive or delete
  const canEdit = !currentUser || (project.contributors || [project.userId]).includes(currentUser?.uid);

  // Lock body scroll when mobile edit sheet is open
  useEffect(() => {
    if (showMobileEdit) {
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    } else {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    };
  }, [showMobileEdit]);
  const areaRefs = useRef({});

  useEffect(() => {
    const handler = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const areas    = project.areas || [];
  const internal = areas.filter(a => a.type === 'internal');
  const external = areas.filter(a => a.type === 'external');
  const wf = project.workflow || WORKFLOW.MEASURING;

  // Role — driven by prop from App.js, survives navigation
  // Detect role capabilities
  const userRoles = currentUser?.roles || [];
  const canMeasure = userRoles.includes('measurer') || userRoles.length === 0; // default = measurer
  const canAssess = userRoles.includes('assessor');
  const isDualRole = canMeasure && canAssess;

  // Prompt when project workflow doesn't match current role
  const [rolePrompt, setRolePrompt] = useState(null);
  useEffect(() => {
    const isAssessWf = wf === WORKFLOW.READY_TO_ASSESS || wf === WORKFLOW.ASSESSING || wf === WORKFLOW.ASSESSED;
    if (isAssessWf && activeRole === 'measurer') {
      if (isDualRole) {
        setRolePrompt({ type: 'switch_role', target: 'assessor' });
      } else if (!canMeasure) {
        // Assessor-only user — project already in assess mode, just switch view
        onSetActiveRole('assessor');
      }
    } else if (wf === WORKFLOW.MEASURING && activeRole === 'assessor') {
      if (isDualRole) {
        setRolePrompt({ type: 'switch_role', target: 'measurer' });
      } else if (!canAssess) {
        // Measurer-only user — project in measuring mode, just switch view
        onSetActiveRole('measurer');
      } else {
        // Assessor-only user, project in measuring mode — offer to advance workflow
        setRolePrompt({ type: 'advance_workflow' });
      }
    }
  }, [project.firestoreId]); // eslint-disable-line react-hooks/exhaustive-deps
  const isAssessor = activeRole === 'assessor';
  const isMeasurer = !isAssessor;
  // Measurers always stay in measuring mode regardless of workflow state
  const effectiveWf = isMeasurer ? WORKFLOW.MEASURING : wf;

  const requestDuplicate = (area) => { onDuplicateArea(area); };

  const requestDeleteArea = (area) => setConfirm({
    title: 'Delete area',
    message: `Delete "${area.name || 'this area'}"? This cannot be undone.`,
    confirmLabel: 'Delete',
    danger: true,
    onConfirm: () => { setConfirm(null); onDeleteArea(area.firestoreId); },
  });

  const archiveTimerRef = React.useRef(null);
  const handleArchive = () => {
    setArchiveToast(true);
    archiveTimerRef.current = setTimeout(() => {
      setArchiveToast(false);
      onArchive();
    }, 5000);
  };
  const undoArchive = () => {
    clearTimeout(archiveTimerRef.current);
    setArchiveToast(false);
  };

  const requestDeleteProject = () => setConfirm({
    title: 'Delete project',
    message: 'Delete this project and all its areas? This cannot be undone.',
    confirmLabel: 'Delete',
    danger: true,
    onConfirm: () => { setConfirm(null); onDelete(); },
  });

  const requestReadyToAssess = () => {
    // Check which areas are incomplete
    const areasWithSurfaces = areas.filter(a => (a.surfaceEntries || []).length > 0);
    const areasNoSurfaces = areas.filter(a => (a.surfaceEntries || []).length === 0);
    const incompleteAreas = areasWithSurfaces.filter(a => {
      if (a.status === 'finished' || a.manualComplete) return false;
      const entries = a.surfaceEntries || [];
      return !entries.every(e => isUserMeasured(e.substrate, e));
    });

    // If all areas are complete (or no areas at all), go straight to confirmation
    if (incompleteAreas.length === 0 && areasNoSurfaces.length === 0) {
      confirmReadyToAssess();
      return;
    }

    // Build description of incomplete areas
    const issues = [];
    areasNoSurfaces.forEach(a => {
      issues.push(`• ${a.name || 'Unnamed'} — no surfaces added`);
    });
    incompleteAreas.forEach(a => {
      const entries = a.surfaceEntries || [];
      const measured = entries.filter(e => isUserMeasured(e.substrate, e)).length;
      issues.push(`• ${a.name || 'Unnamed'} — ${measured} of ${entries.length} surfaces measured`);
    });

    const allIncomplete = [...incompleteAreas, ...areasNoSurfaces];
    const totalIncomplete = allIncomplete.length;
    const totalReady = areas.length - totalIncomplete;

    setConfirm({
      title: `${totalIncomplete} area${totalIncomplete > 1 ? 's' : ''} incomplete`,
      message: `${totalReady} area${totalReady !== 1 ? 's' : ''} ready, ${totalIncomplete} incomplete:\n\n${issues.join('\n')}\n\nYou can go back and finish them, or proceed to assessment anyway. The PM will see which areas are incomplete.`,
      confirmLabel: `Proceed Anyway →`,
      danger: false,
      onConfirm: async () => {
        // Flag incomplete areas as manually accepted
        const idsToFlag = allIncomplete.filter(a => a.firestoreId).map(a => a.firestoreId);
        if (idsToFlag.length > 0 && onAcceptIncompleteAreas) {
          await onAcceptIncompleteAreas(idsToFlag);
        }
        setConfirm(null);
        confirmReadyToAssess();
      },
    });
  };

  const confirmReadyToAssess = () => {
    onSetWorkflow(WORKFLOW.READY_TO_ASSESS);
    const projName = project.projectName || project.clientName || 'Project';
    setConfirm({
      title: '✅ Measurements complete!',
      message: `Great work — this project is now ready for assessment.\n\nNext step: Notify your PM/Assessor so they can begin.`,
      confirmLabel: 'Done',
      danger: false,
      emailAction: {
        subject: `Site2Scope: Ready for assessment — ${projName}`,
        body: `Hi,\n\nThe measurements for "${projName}"${project.jobNumber ? ` (Job #${project.jobNumber})` : ''} are complete and the project is ready for your assessment.\n\nPlease open Site2Scope and search for the project to begin.\n\nYou'll review the photos and scope for each area, then enter your estimated paint hours per surface.\n\nThanks`,
      },
      onConfirm: () => setConfirm(null),
    });
  };

  const requestBackToMeasuring = () => setConfirm({
    title: 'Return to measuring',
    message: 'This will move the project back to Measuring status. Any assessment data already entered will be kept.',
    confirmLabel: 'Revert',
    danger: false,
    onConfirm: () => { setConfirm(null); onSetWorkflow(WORKFLOW.MEASURING); },
  });

  const jumpTo = (id) => {
    setJumpNavOpen(false);
    setTimeout(() => {
      const el = areaRefs.current[id];
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const card = el.querySelector('.pd-area-card');
        if (card) {
          card.classList.add('pd-area-highlight');
          setTimeout(() => card.classList.remove('pd-area-highlight'), 1800);
        }
      }
    }, 150);
  };

  const areaList = (list) => list.map(a => (
    <div key={a.firestoreId || a.id} ref={el => { areaRefs.current[a.firestoreId || a.id] = el; }}>
      <AreaCard
        area={a}
        onEdit={onEditArea}
        onRequestDelete={requestDeleteArea}
        onRequestDuplicate={requestDuplicate}
        projectWorkflow={effectiveWf}
        onAssessArea={(area) => onAssess(area.firestoreId)}
      />
    </div>
  ));

  // Check if all measurable areas have been fully assessed (even if workflow hasn't advanced)
  const measurableAreas = areas.filter(a => (a.surfaceEntries || []).length > 0);
  const allAreasAssessed = measurableAreas.length > 0 && measurableAreas.every(a => {
    const ad = a.assessBySubstrate || {};
    const measured = [...new Set((a.surfaceEntries || []).map(e => e.substrate).filter(Boolean))];
    const pmAdded = (a.pmAddedSurfaces || []).filter(s => !measured.includes(s));
    const allSubs = [...measured, ...pmAdded];
    return allSubs.length > 0 && allSubs.every(s => parseFloat(ad[s]?.hours) > 0);
  });
  const anyAreaPartiallyAssessed = measurableAreas.some(a => {
    const ad = a.assessBySubstrate || {};
    return Object.values(ad).some(v => parseFloat(v.hours) > 0);
  });

  // Dynamic assessment status label — data-driven, not just workflow field
  const assessStatus = () => {
    if (wf === WORKFLOW.READY_TO_ASSESS || wf === WORKFLOW.ASSESSING) {
      if (allAreasAssessed) return { label: 'All Areas Assessed', color: '#059669', icon: '✅' };
      if (anyAreaPartiallyAssessed) return { label: 'Assessing', color: '#d97706', icon: '📋' };
      return { label: 'Ready to Assess', color: '#E8871B', icon: '📋' };
    }
    if (wf === WORKFLOW.ASSESSED) return { label: 'All Areas Assessed', color: '#059669', icon: '✅' };
    if (wf === WORKFLOW.COMPARING) return { label: 'Comparing Hours', color: '#7c3aed', icon: '⚖️' };
    if (wf === WORKFLOW.APPROVED) return { label: 'Hours Adjusted', color: '#16a34a', icon: '✅' };
    return null;
  };
  const assessInfo = assessStatus();

  // Workflow action bar — driven by USER ROLE, not just workflow state
  // Measurers stay in measuring view even after marking "Ready to Assess"
  // Assessors see assessment controls
  const measBadge = (() => {
    const areasWithSurfaces = areas.filter(a => (a.surfaceEntries || []).length > 0);
    const fullyMeasured = areasWithSurfaces.filter(a =>
      (a.surfaceEntries || []).every(e => isUserMeasured(e.substrate, e))
    );
    const allDone = fullyMeasured.length >= areasWithSurfaces.length && areasWithSurfaces.length > 0;
    const label = allDone ? 'All Areas Measured' : areasWithSurfaces.length > 0 ? 'Measuring' : 'Ready to Measure';
    const color = allDone ? '#059669' : areasWithSurfaces.length > 0 ? '#5B2D82' : '#94a3b8';
    const icon = allDone ? '✅' : '📐';
    return { label, color, icon };
  })();

  const workflowBarJsx = (
    <div className="pd-workflow-bar">
      {isMeasurer ? (
        <>
          {/* Measuring badge — always visible for measurers */}
          <div className="pd-wf-badge" style={{ background: measBadge.color }}>{measBadge.icon} {measBadge.label}</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {/* Assessment status indicator — shows after marking ready */}
            {wf !== WORKFLOW.MEASURING && (
              <span className="pd-wf-status-pill" style={{ background: assessInfo?.color || '#E8871B' }}>
                {assessInfo?.icon} {assessInfo?.label}
              </span>
            )}
            {areas.length > 0 && wf === WORKFLOW.MEASURING && (
              <button className="pd-wf-btn pd-wf-ready" onClick={requestReadyToAssess}>📋 Ready to Assess</button>
            )}
            {wf !== WORKFLOW.MEASURING && (
              <button className="pd-wf-btn pd-wf-revert" onClick={requestBackToMeasuring}>← Back to Measuring</button>
            )}
          </div>
        </>
      ) : (
        <>
          {/* Assessment badge — always visible for assessors */}
          <div className="pd-wf-badge" style={{ background: assessInfo?.color || '#5B2D82' }}>{assessInfo?.icon || '📋'} {assessInfo?.label || 'Assessment'}</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {/* Measuring status — read-only for assessors */}
            <span className="pd-wf-status-pill" style={{ background: measBadge.color }}>
              {measBadge.icon} {measBadge.label}
            </span>
            {(wf === WORKFLOW.READY_TO_ASSESS || wf === WORKFLOW.ASSESSING) && onAssess && areas.some(a => (a.surfaceEntries || []).length > 0) && (
              <button className="pd-wf-btn" style={{ background: allAreasAssessed ? '#059669' : '#d97706' }} onClick={() => onAssess(null)}>
                {allAreasAssessed ? '✅ Complete → Notify BDM' : '📋 Assess'}
              </button>
            )}
            <button className="pd-wf-btn pd-wf-revert" onClick={requestBackToMeasuring}>← Back to Measuring</button>
          </div>
        </>
      )}
    </div>
  );

  return (
    <div className="s2s-screen">
      {confirm && (
        <ConfirmModal
          title={confirm.title}
          message={confirm.message}
          confirmLabel={confirm.confirmLabel}
          danger={confirm.danger}
          emailAction={confirm.emailAction}
          onConfirm={confirm.onConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}

      {/* Role / workflow mismatch prompt */}
      {rolePrompt && rolePrompt.type === 'switch_role' && (
        <ConfirmModal
          title={rolePrompt.target === 'assessor' ? '📋 Switch to Assessor mode?' : '📐 Switch to Measurer mode?'}
          message={rolePrompt.target === 'assessor'
            ? 'This project is ready for assessment. Would you like to switch to Assessor mode to enter hours?'
            : 'This project is still being measured. Would you like to switch to Measurer mode?'}
          confirmLabel={rolePrompt.target === 'assessor' ? 'Switch to Assessor' : 'Switch to Measurer'}
          onConfirm={() => { onSetActiveRole(rolePrompt.target); setRolePrompt(null); }}
          onCancel={() => setRolePrompt(null)}
        />
      )}
      {rolePrompt && rolePrompt.type === 'advance_workflow' && (
        <ConfirmModal
          title="📋 Move project to Assessment?"
          message="This project is still in measuring mode, but you're signed in as an Assessor. Would you like to advance it to assessment mode so you can enter hours?"
          confirmLabel="Move to Assessment"
          onConfirm={() => { onSetWorkflow(WORKFLOW.READY_TO_ASSESS); setRolePrompt(null); }}
          onCancel={() => { setRolePrompt(null); onBack(); }}
        />
      )}

      {/* ── Header ── */}
      <div className="s2s-header">
        <button className="s2s-back-btn" onClick={onBack}><Icon name="arrow-left" size={16} /> Back</button>
        <div className="s2s-header-center">
          <div className="s2s-header-title">
            {project.jobNumber && <span className="pl-job-num">#{project.jobNumber}</span>}
            {project.jobNumber && ' — '}
            {project.projectName || project.clientName || 'Project'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {!isDesktop && (
            <button className="s2s-btn-edit-sm" onClick={() => setShowMobileEdit(true)} title="Edit project details" aria-label="Edit project details"><Icon name="edit" size={16} /></button>
          )}
          <button className="pd-profile-chip" onClick={() => { setShowProfile(v => !v); setEditingName(false); setNameInput(currentUser?.displayName || ''); }}
            title={isAssessor ? 'Assessor' : 'Measurer'}>
            <span className="pd-profile-initial">{(currentUser?.displayName || currentUser?.email || '?')[0].toUpperCase()}</span>
            <span className="pd-profile-role-dot" style={{ background: isAssessor ? '#d97706' : '#5B2D82' }} />
          </button>
        </div>
      </div>

      {workflowBarJsx}

      {/* ── Profile panel — dropdown on desktop, bottom sheet on mobile ── */}
      {showProfile && (
        <div className={isDesktop ? "pd-profile-dropdown-wrap" : "pd-mobile-edit-overlay"} onClick={() => setShowProfile(false)}>
          <div className={isDesktop ? "pd-profile-dropdown" : "pd-mobile-edit-sheet"} onClick={e => e.stopPropagation()} style={!isDesktop ? { maxHeight: 320 } : undefined}>
            {!isDesktop && (
              <div className="pd-mobile-edit-header">
                <span className="pd-mobile-edit-title">👤 Profile</span>
                <button className="pd-mobile-edit-close" onClick={() => setShowProfile(false)} aria-label="Close">✕</button>
              </div>
            )}
            <div style={{ padding: isDesktop ? '14px 16px' : '16px 20px' }}>
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Signed in as</div>
                {editingName ? (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input
                      value={nameInput}
                      onChange={e => setNameInput(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && nameInput.trim()) { onUpdateDisplayName?.(nameInput.trim()); setEditingName(false); } if (e.key === 'Escape') setEditingName(false); }}
                      style={{ flex: 1, padding: '7px 10px', border: '2px solid #5B2D82', borderRadius: 8, fontSize: 14, fontFamily: 'inherit' }}
                      autoFocus
                    />
                    <button
                      onClick={async () => { if (nameInput.trim() && onUpdateDisplayName) { await onUpdateDisplayName(nameInput.trim()); } setEditingName(false); }}
                      style={{ padding: '7px 12px', background: '#5B2D82', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>
                      Save
                    </button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 15, fontWeight: 600, color: '#1e293b' }}>{currentUser?.displayName || currentUser?.email || 'User'}</span>
                    <button onClick={() => setEditingName(true)} style={{ fontSize: 12, color: '#5B2D82', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 6px' }}>✏️</button>
                  </div>
                )}
                {currentUser?.email && <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{currentUser.email}</div>}
                {isDualRole ? (
                  <div className="pd-role-toggle" style={{ marginTop: 8 }}>
                    <button
                      className={`pd-role-btn ${activeRole === 'measurer' ? 'pd-role-btn-active' : ''}`}
                      style={activeRole === 'measurer' ? { background: '#5B2D82', color: '#fff' } : {}}
                      onClick={() => { onSetActiveRole('measurer'); setShowProfile(false); }}>
                      📐 Measurer
                    </button>
                    <button
                      className={`pd-role-btn ${activeRole === 'assessor' ? 'pd-role-btn-active' : ''}`}
                      style={activeRole === 'assessor' ? { background: '#d97706', color: '#fff' } : {}}
                      onClick={() => { onSetActiveRole('assessor'); setShowProfile(false); }}>
                      📋 Assessor
                    </button>
                  </div>
                ) : (
                  <div style={{ fontSize: 11, fontWeight: 700, marginTop: 6, textTransform: 'uppercase', letterSpacing: '0.5px', color: isAssessor ? '#d97706' : '#5B2D82' }}>
                    <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: isAssessor ? '#d97706' : '#5B2D82', marginRight: 6 }} />
                    {isAssessor ? '📋 Assessor' : '📐 Measurer'}
                  </div>
                )}
              </div>
              {onSignOut && (
                <button
                  onClick={() => { setShowProfile(false); onSignOut(); }}
                  style={{ width: '100%', padding: '10px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: 10, fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
                  Sign Out
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {showMobileEdit && (
        <div className="pd-mobile-edit-overlay" onClick={() => setShowMobileEdit(false)}>
          <div className="pd-mobile-edit-sheet" onClick={e => e.stopPropagation()}>
            <div className="pd-mobile-edit-header">
              <span className="pd-mobile-edit-title">Edit Project Details</span>
              <button className="pd-mobile-edit-close" onClick={() => setShowMobileEdit(false)} aria-label="Close">✕</button>
            </div>
            <div style={{ padding: '16px 20px 0' }}>
              <ProjectDetailsEditor
                project={project}
                onSave={async (updated) => { await onSaveDetails(updated); setShowMobileEdit(false); }}
                onCancel={() => setShowMobileEdit(false)}
              />
            </div>
            <div className="pd-mobile-edit-danger-row">
              <button className="pd-mobile-archive-btn" onClick={() => { setShowMobileEdit(false); handleArchive(); }}>
                <Icon name="archive" size={14} /> Archive Project
              </button>
              {canEdit && (
                <button className="pd-mobile-delete-btn" onClick={() => { setShowMobileEdit(false); requestDeleteProject(); }}>
                  <Icon name="trash" size={14} /> Delete Project
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Body — desktop: two-column, mobile: single column ── */}
      <div className={`pd-body ${isDesktop ? 'pd-desktop' : 'pd-mobile'}`}>

        {/* ── Left: areas ── */}
        <div className="pd-areas-col">

          {/* PM-added areas notification — visible to measurers */}
          {isMeasurer && (() => {
            const pmAreas = areas.filter(a => a.pmAdded && (!a.surfaceEntries || a.surfaceEntries.length === 0));
            if (pmAreas.length === 0) return null;
            return (
              <div className="pd-pm-alert">
                <div className="pd-pm-alert-icon">⚠️</div>
                <div className="pd-pm-alert-body">
                  <div className="pd-pm-alert-title">PM added {pmAreas.length} area{pmAreas.length > 1 ? 's' : ''} that need{pmAreas.length === 1 ? 's' : ''} measurements</div>
                  <div className="pd-pm-alert-list">{pmAreas.map(a => (
                    <span key={a.firestoreId || a.id} className="pd-pm-alert-area">{a.name || 'Unnamed'}</span>
                  ))}</div>
                  {pmAreas[0]?.pmNote && <div className="pd-pm-alert-note">📝 {pmAreas[0].pmNote}</div>}
                </div>
              </div>
            );
          })()}

          {/* Add buttons — only while workflow is still in measuring */}
          {wf === WORKFLOW.MEASURING && (
          <div className="pd-add-row">
            <button className="pd-add-big internal" onClick={() => onNewArea('internal')}>
              <span className="pd-add-icon">🏠</span>
              <div>
                <div className="pd-add-title">Add Internal</div>
                <div className="pd-add-sub">Room, corridor, bathroom</div>
              </div>
            </button>
            <button className="pd-add-big external" onClick={() => onNewArea('external')}>
              <span className="pd-add-icon">🏗️</span>
              <div>
                <div className="pd-add-title">Add External</div>
                <div className="pd-add-sub">Elevation, facade, fence</div>
              </div>
            </button>
          </div>
          )}

          {/* Assessment guidance */}
          {(wf === WORKFLOW.READY_TO_ASSESS || wf === WORKFLOW.ASSESSING) && areas.length > 0 && !allAreasAssessed && (
            isAssessor ? (
              <div className="pd-assess-hint">
                📋 Select an area below to begin entering your assessed hours
              </div>
            ) : (
              <div className="pd-assess-hint">
                ✅ Sent for assessment. You can still tap an area to review or edit your measurements.
              </div>
            )
          )}
          {(wf === WORKFLOW.READY_TO_ASSESS || wf === WORKFLOW.ASSESSING) && areas.length > 0 && allAreasAssessed && isAssessor && (
            <div className="pd-assess-complete-card">
              <div className="pd-acc-header">
                <span className="pd-acc-icon">✅</span>
                <div>
                  <div className="pd-acc-title">Assessment complete ✓</div>
                  <div className="pd-acc-sub">Let the BDM/Estimator know so they can review the assessed hours</div>
                </div>
              </div>
              <button className="pd-acc-btn" onClick={() => onAssess(null)}>
                ✉️ Notify BDM Now →
              </button>
            </div>
          )}
          {(wf === WORKFLOW.READY_TO_ASSESS || wf === WORKFLOW.ASSESSING) && areas.length > 0 && allAreasAssessed && isMeasurer && (
            <div className="pd-assess-hint pd-assess-hint-done">
              ✅ All areas assessed — awaiting assessor to confirm and notify
            </div>
          )}
          {anyAreaPartiallyAssessed && areas.length > 0 && wf !== WORKFLOW.APPROVED && (
            <div className="pd-review-card">
              <div className="pd-review-card-icon">⚖️</div>
              <div className="pd-review-card-body">
                <div className="pd-review-card-title">
                  {wf === WORKFLOW.COMPARING ? 'Comparing Hours' :
                   allAreasAssessed ? 'Assessment Complete — Compare Hours' :
                   'Compare Hours'}
                </div>
                <div className="pd-review-card-desc">
                  {allAreasAssessed
                    ? (isAssessor
                        ? 'Compare measured vs assessed hours side by side. View the BDM\'s adjusted figures as they\'re entered.'
                        : 'Compare measured vs assessed hours per surface. Accept, adjust, or override the PM\'s assessed figures.')
                    : `${measurableAreas.filter(a => Object.values(a.assessBySubstrate || {}).some(v => parseFloat(v.hours) > 0)).length} of ${measurableAreas.length} areas have assessment data — compare progress so far.`
                  }
                </div>
              </div>
              <button className="pd-review-card-btn" onClick={onReview}>
                ⚖️ Compare Hours →
              </button>
            </div>
          )}
          {wf === WORKFLOW.APPROVED && areas.length > 0 && (
            <div className="pd-review-card pd-review-card-done">
              <div className="pd-review-card-icon">✅</div>
              <div className="pd-review-card-body">
                <div className="pd-review-card-title">Hours Adjusted</div>
                <div className="pd-review-card-desc">Adjusted hours have been finalised. You can still view or amend them.</div>
              </div>
              <button className="pd-review-card-btn" onClick={onReview}>⚖️ Compare Hours →</button>
            </div>
          )}

          {/* Area lists */}
          {areas.length === 0 ? (
            <div className="empty-state" style={{padding:'40px 20px'}}>
              <div className="empty-state-icon"><Icon name="layers" size={56} /></div>
              <div className="empty-state-title">No areas yet</div>
              <div className="empty-state-desc">Use the buttons above to add internal or external areas and start measuring</div>
            </div>
          ) : (
            <div className="pd-area-list">
              {internal.length > 0 && (
                <div className="pd-area-group">
                  <div className="pd-group-label pd-group-label-sticky">
                    <span>🏠 Internal <span className="pd-group-count">({internal.length})</span></span>
                    <span className="pd-group-done">{internal.filter(a => a.status === 'finished').length}/{internal.length} done</span>
                  </div>
                  {areaList(internal)}
                </div>
              )}
              {external.length > 0 && (
                <div className="pd-area-group">
                  <div className="pd-group-label pd-group-label-sticky">
                    <span>🏗️ External <span className="pd-group-count">({external.length})</span></span>
                    <span className="pd-group-done">{external.filter(a => a.status === 'finished').length}/{external.length} done</span>
                  </div>
                  {areaList(external)}
                </div>
              )}
            </div>
          )}

          <div style={{ height: 40 }} />
        </div>

        {/* ── Jump-to-area FAB (mobile, 3+ areas) ── */}
        {!isDesktop && areas.length >= 3 && (
          <button className="pd-jump-fab" onClick={() => setJumpNavOpen(true)} title="Jump to area">
            ☰ Areas
          </button>
        )}

        {/* ── Jump nav drawer ── */}
        {jumpNavOpen && (
          <div className="pd-jump-overlay" onClick={() => setJumpNavOpen(false)}>
            <div className="pd-jump-drawer" onClick={e => e.stopPropagation()}>
              <div className="pd-jump-header">
                <span>Jump to Area</span>
                <button className="pd-jump-close" onClick={() => setJumpNavOpen(false)} aria-label="Close navigation">✕</button>
              </div>
              {areas.map((a, i) => (
                <button key={a.firestoreId || a.id} className="pd-jump-item" onClick={() => jumpTo(a.firestoreId || a.id)}>
                  <span className="pd-jump-num">{i + 1}</span>
                  <span className="pd-jump-icon">{a.type === 'internal' ? '🏠' : '🏗️'}</span>
                  <span className="pd-jump-name">{a.name || 'Unnamed Area'}</span>
                  {a.status === 'finished' && <span className="pd-jump-done-badge">✓</span>}
                </button>
              ))}
              <div style={{ height: 24 }} />
            </div>
          </div>
        )}

        {/* ── Right: export panel (desktop only) ── */}
        {isDesktop && (
          <div className="pd-export-col">
            <ExportPanel project={project} onEditArea={onEditArea} onSaveDetails={onSaveDetails} onReview={onReview} workflow={wf} onArchive={handleArchive} onDelete={requestDeleteProject} canEdit={canEdit} currentUser={currentUser} onAssess={onAssess} allAreasAssessed={allAreasAssessed} onLogActivity={onLogActivity} activeRole={activeRole} />
          </div>
        )}
      </div>

      {/* ── Archive undo toast ── */}
      {archiveToast && (
        <div className="pd-archive-toast">
          <span>📦 Archiving project…</span>
          <button className="pd-archive-undo" onClick={undoArchive}>Undo</button>
        </div>
      )}
    </div>
  );
}

export default ProjectDetail;
